import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { passwordResetApi } from '../api/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const fullEmail = email.includes('@') ? email.toLowerCase() : `${email.toLowerCase()}@mypatriotmail.org`;
    
    if (!fullEmail.endsWith('@mypatriotmail.org')) {
      setError('Only @mypatriotmail.org emails are allowed');
      return;
    }

    setLoading(true);
    try {
      await passwordResetApi.forgotPassword(fullEmail);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#16162a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src="/mpm-logo.png" 
            alt="MyPatriotMail" 
            className="w-20 h-20 object-contain mb-4 drop-shadow-[0_0_15px_rgba(109,74,255,0.5)]"
          />
          <h1 className="text-2xl font-bold text-white">MyPatriotMail</h1>
          <p className="text-gray-400 text-sm mt-1">Encrypted Email Platform</p>
        </div>

        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-white">
              {success ? 'Check Your Email' : 'Forgot Password'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {success 
                ? 'We sent you a password reset link' 
                : 'Enter your email to receive a reset link'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <p className="text-gray-400 text-center text-sm">
                  If an account exists for <span className="text-white font-medium">{email.includes('@') ? email : `${email}@mypatriotmail.org`}</span>, 
                  you will receive an email with instructions to reset your password.
                </p>
                <p className="text-gray-500 text-center text-xs">
                  The link will expire in 1 hour. Check your spam folder if you don't see it.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="email"
                      type="text"
                      placeholder="username or full email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                      required
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Enter your username or full @mypatriotmail.org email
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Link 
              to="/login" 
              className="flex items-center gap-2 text-sm text-[#6D4AFF] hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
